import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { articlesAPI, categoriesAPI, aiAPI, workflowAPI, versionsAPI, preflightAPI, aiToolsAPI, slugsAPI, autosaveAPI, cloneAPI, commentCloseAPI, publicAPI, seriesAPI, annotationsAPI, aiExtrasAPI, autosaveRecoveryAPI, peerApproveAPI, factCheckAPI, internalLinksAPI, i18nAPI, aiV2API, blocksAPI, aiV7API, headlineExpAPI } from '../../lib/api';
import { AIModelSelector } from '../../components/AIModelSelector';
import { ChartBlockBuilder } from '../../components/ChartBlockBuilder';
import { useAuth } from '../../lib/auth-context';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  ArrowLeft, Save, Sparkles, Wand2, Tag, Image as ImageIcon,
  GitBranch, Search, CheckCircle, XCircle, RotateCcw, Send,
  History, Lock, Unlock, AlertTriangle, Flag, Clock, Shield,
  FileText, Skull, Rocket, Loader2, Link2, Copy, MessageSquareOff,
  MapPin, Users, Layers, Database, MessageCircle, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import MDEditor from '@uiw/react-md-editor';
import { BlockEditor } from '../../components/BlockEditor';
import SlaTimer from '../../components/workflow/SlaTimer';
import AnnotationsPanel from '../../components/workflow/AnnotationsPanel';
import ApprovalChain from '../../components/workflow/ApprovalChain';
import InlineSelectionToolbar from '../../components/workflow/InlineSelectionToolbar';
import api from '../../lib/api';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: '#5B616E', bg: '#5B616E' },
  in_review: { label: 'In Review', color: '#9A6B1A', bg: '#9A6B1A' },
  sub_edited: { label: 'Sub-Edited', color: '#1E5A8A', bg: '#1E5A8A' },
  legal_review: { label: 'Legal Review', color: '#4A1D2A', bg: '#4A1D2A' },
  approved: { label: 'Approved', color: '#1F7A5A', bg: '#1F7A5A' },
  scheduled: { label: 'Scheduled', color: '#B89B5E', bg: '#B89B5E' },
  published: { label: 'Published', color: '#1F7A5A', bg: '#1F7A5A' },
  archived: { label: 'Archived', color: '#5B616E', bg: '#5B616E' },
  killed: { label: 'Killed', color: '#B3261E', bg: '#B3261E' },
  review: { label: 'In Review', color: '#9A6B1A', bg: '#9A6B1A' },
};

const PRIORITY_CONFIG = {
  standard: { label: 'Standard', color: '#5B616E' },
  feature: { label: 'Feature', color: '#1E5A8A' },
  evergreen: { label: 'Evergreen', color: '#1F7A5A' },
  urgent: { label: 'Urgent', color: '#9A6B1A' },
  breaking: { label: 'Breaking', color: '#B3261E' },
};

export default function AdminArticleEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [workflowHistory, setWorkflowHistory] = useState([]);
  const [showWorkflowDialog, setShowWorkflowDialog] = useState(false);
  const [workflowAction, setWorkflowAction] = useState('');
  const [workflowComment, setWorkflowComment] = useState('');
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [articlePriority, setArticlePriority] = useState('standard');
  const [isLocked, setIsLocked] = useState(false);
  const [sidebarTab, setSidebarTab] = useState('publish');

  const [editionOptions, setEditionOptions] = useState([]);
  const [seriesList, setSeriesList] = useState([]);
  const [annotations, setAnnotations] = useState([]);
  const [showAnnotationPanel, setShowAnnotationPanel] = useState(false);
  const [newAnnotation, setNewAnnotation] = useState({ text_selection: '', note: '', annotation_type: 'editorial' });
  // Draft recovery
  const [draftRecovery, setDraftRecovery] = useState(null);
  // Diff viewer
  const [showDiff, setShowDiff] = useState(false);
  const [diffData, setDiffData] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffV1, setDiffV1] = useState(null);
  const [diffV2, setDiffV2] = useState(null);
  // Collaborative presence
  const [presentUsers, setPresentUsers] = useState([]);
  // AI model selector
  const [aiModel, setAiModel] = useState({ provider: null, model: null });
  // Parallel approvals
  const [requiredApprovals, setRequiredApprovals] = useState(1);
  const [articleApprovals, setArticleApprovals] = useState([]);
  const [approvalNote, setApprovalNote] = useState('');
  const [approvingLoading, setApprovingLoading] = useState(false);
  const [form, setForm] = useState({
    title: '', subtitle: '', content: '', category_id: '',
    tags: [], featured_image: '', video_url: '',
    is_featured: false, is_trending: false, is_premium: false,
    status: 'draft', scheduled_at: '',
    edition: 'global',
    // §1 metadata
    story_type: '',
    entities: [],
    location: { name: '', country: '', lat: '', lon: '' },
    expires_at: '',
    custom_meta: '',
    series_id: '',
    canonical_url: '',
    og_title: '',
    og_description: '',
    og_image: '',
    language: 'en',
  });
  const [entityInput, setEntityInput] = useState({ text: '', type: 'person' });
  // Fact-check
  const [factChecks, setFactChecks] = useState([]);
  const [factCheckStatus, setFactCheckStatus] = useState('unchecked');
  const [newFact, setNewFact] = useState('');
  // Internal links + i18n
  const [internalLinkSuggestions, setInternalLinkSuggestions] = useState([]);
  const [languages, setLanguages] = useState([{ code: 'en', name: 'English' }]);
  const [tagInput, setTagInput] = useState('');
  const [editorMode, setEditorMode] = useState('markdown');
  const [blocks, setBlocks] = useState([]);
  const [preflightResult, setPreflightResult] = useState(null);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [slug, setSlug] = useState('');
  const [autoTagLoading, setAutoTagLoading] = useState(false);
  const [seoGenLoading, setSeoGenLoading] = useState(false);
  const [seoMeta, setSeoMeta] = useState({ title: '', description: '' });
  const [autosaveTimer, setAutosaveTimer] = useState(null);
  const [lastAutosave, setLastAutosave] = useState(null);
  const [commentsClosed, setCommentsClosed] = useState(false);
  // Headline assistant (inline popover next to the title input)
  const [headlineOpen, setHeadlineOpen] = useState(false);
  const [headlineLoading, setHeadlineLoading] = useState(false);
  const [headlineSuggestions, setHeadlineSuggestions] = useState([]);
  // Social post generator dialog
  const [socialOpen, setSocialOpen] = useState(false);
  const [socialLoading, setSocialLoading] = useState(false);
  const [socialPosts, setSocialPosts] = useState({});

  useEffect(() => {
    categoriesAPI.list().then(r => setCategories(r.data.categories || [])).catch(() => {});
    publicAPI
      .editions()
      .then((r) => {
        const eds = r.data.editions || [];
        setEditionOptions([{ id: 'global', name: 'International (all editions)', slug: 'global' }, ...eds]);
      })
      .catch(() => setEditionOptions([{ id: 'global', name: 'International (all editions)', slug: 'global' }]));
    seriesAPI.list().then(r => setSeriesList(r.data.series || [])).catch(() => {});
    i18nAPI.getLanguages().then(r => setLanguages((r.data.languages || []).filter(l => l.enabled !== false))).catch(() => {});
    if (isEdit) loadArticle();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Auto-save every 30 seconds
  useEffect(() => {
    if (!isEdit) return;
    const timer = setInterval(() => {
      if (form.title || form.content) {
        autosaveAPI.save(id, { title: form.title, content: form.content })
          .then(() => setLastAutosave(new Date()))
          .catch(() => {});
      }
    }, 30000);
    return () => clearInterval(timer);
  }, [isEdit, id, form.title, form.content]);

  const loadArticle = async () => {
    try {
      const res = await articlesAPI.get(id);
      const a = res.data;
      setWorkflowHistory(a.workflow_history || []);
      setArticlePriority(a.priority || 'standard');
      setIsLocked(!!a.locked_by && a.locked_by !== user?.id);
      setForm({
        title: a.title || '', subtitle: a.subtitle || '',
        content: a.content || '', category_id: a.category_id || '',
        tags: a.tags || [], featured_image: a.featured_image || '',
        video_url: a.video_url || '', is_featured: a.is_featured || false,
        is_trending: a.is_trending || false, is_premium: a.is_premium || false,
        status: a.status || 'draft', scheduled_at: a.scheduled_at || '',
        edition: a.edition || 'global',
        story_type: a.story_type || '',
        entities: a.entities || [],
        location: a.location || { name: '', country: '', lat: '', lon: '' },
        expires_at: a.expires_at ? a.expires_at.slice(0, 16) : '',
        custom_meta: a.custom_meta ? JSON.stringify(a.custom_meta, null, 2) : '',
        series_id: a.series_id || '',
        canonical_url: a.canonical_url || '',
        og_title: a.og_title || '',
        og_description: a.og_description || '',
        og_image: a.og_image || '',
        language: a.language || 'en',
      });
      setRequiredApprovals(a.required_approvals || 1);
      setArticleApprovals(a.approvals || []);
      // Load annotations + fact-checks
      annotationsAPI.list(id).then(r => setAnnotations(r.data.annotations || [])).catch(() => {});
      factCheckAPI.get(id).then(r => { setFactChecks(r.data.fact_checks || []); setFactCheckStatus(r.data.fact_check_status || 'unchecked'); }).catch(() => {});
      internalLinksAPI.suggest(id).then(r => setInternalLinkSuggestions(r.data.suggestions || [])).catch(() => {});
      // Check for draft recovery
      autosaveRecoveryAPI.get(id).then(r => {
        if (r.data.is_newer && r.data.autosave_content) {
          setDraftRecovery(r.data);
        }
      }).catch(() => {});
      // Load versions
      versionsAPI.list(id).then(r => setVersions(r.data.versions || [])).catch(() => {});
      // Lock article
      versionsAPI.lock(id).catch(() => {});
      // Check if comments are closed
      setCommentsClosed(!!a.comments_closed);
      // Load structured blocks if any
      blocksAPI.get(id).then(r => {
        const bs = r.data?.blocks || [];
        if (bs.length) {
          setBlocks(bs);
          if (a.has_blocks || (Array.isArray(a.blocks) && a.blocks.length)) setEditorMode('blocks');
        }
      }).catch(() => {});
    } catch { toast.error('Failed to load article'); }
    finally { setLoading(false); }
  };

  // Presence WebSocket + CRDT op sync
  const [collabRevision, setCollabRevision] = useState(0);
  const [remoteOps, setRemoteOps] = useState([]);
  const collabWsRef = React.useRef(null);

  useEffect(() => {
    if (!isEdit || !id || !user) return;
    const wsUrl = (process.env.REACT_APP_BACKEND_URL || window.location.origin)
      .replace(/^http/, 'ws')
      .replace(/\/$/, '') + `/api/ws/article/${id}`;
    let ws;
    try {
      ws = new WebSocket(wsUrl);
      collabWsRef.current = ws;
      ws.onopen = () => {
        ws.send(JSON.stringify({ user_id: user.id || user.sub, user_name: user.full_name || 'Editor', user_email: user.email || '' }));
      };
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === 'presence') setPresentUsers(msg.users || []);
          if (msg.type === 'op') {
            // Remote OT operation — apply to content if not from self
            if (msg.user_id !== (user.id || user.sub)) {
              setRemoteOps(prev => [...prev.slice(-9), msg]);
            }
          }
        } catch {}
      };
    } catch {}

    // Poll for remote ops every 5s while others are editing
    const opPoll = setInterval(async () => {
      if (presentUsers.filter(u => u.user_id !== (user.id || user.sub)).length > 0) {
        try {
          const { crdtAPI } = await import('../../lib/api');
          const r = await crdtAPI.getOps(id, collabRevision);
          if (r.data.ops && r.data.ops.length > 0) {
            const newOps = r.data.ops.filter(op => op.user_id !== (user.id || user.sub));
            if (newOps.length > 0) {
              setRemoteOps(prev => [...prev.slice(-4), ...newOps]);
              setCollabRevision(r.data.total);
            }
          }
        } catch {}
      }
    }, 5000);

    return () => {
      clearInterval(opPoll);
      if (ws) try { ws.close(); } catch {}
      collabWsRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id, user]);

  // Broadcast a content-change op (debounced)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const broadcastOp = React.useCallback(
    (() => {
      let timer = null;
      return (content, prev) => {
        clearTimeout(timer);
        timer = setTimeout(async () => {
          if (!id || !isEdit) return;
          try {
            const { crdtAPI } = await import('../../lib/api');
            await crdtAPI.submitOp(id, {
              type: 'update',
              text: content.slice(-50),
              length: content.length - prev.length,
              user_id: user?.sub || user?.id,
              user_email: user?.email || '',
              revision: collabRevision,
            });
          } catch {}
        }, 2000);
      };
    })(),
    [id, isEdit, user, collabRevision]
  );

  const handleShowDiff = async (v1, v2) => {
    if (!v1 || !v2) return;
    setDiffLoading(true);
    setShowDiff(true);
    setDiffV1(v1);
    setDiffV2(v2);
    try {
      const r = await versionsAPI.diff(id, v1, v2);
      setDiffData(r.data);
    } catch { toast.error('Failed to load diff'); setShowDiff(false); }
    finally { setDiffLoading(false); }
  };

  const handlePeerApprove = async () => {
    setApprovingLoading(true);
    try {
      const r = await peerApproveAPI.approve(id, { note: approvalNote });
      setArticleApprovals(r.data.approvals);
      toast.success(r.data.advanced_to_approved ? 'All approvals complete — article moved to Approved!' : `Your approval recorded (${r.data.approvals.length}/${r.data.required_approvals})`);
      setApprovalNote('');
      if (r.data.advanced_to_approved) loadArticle();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Approval failed'); }
    finally { setApprovingLoading(false); }
  };

  const handleClone = async () => {
    if (!isEdit) return;
    try {
      const res = await cloneAPI.clone(id);
      toast.success('Article cloned!');
      navigate(`/admin/articles/${res.data.id}/edit`);
    } catch { toast.error('Failed to clone'); }
  };

  const handleToggleComments = async () => {
    if (!isEdit) return;
    try {
      const newState = !commentsClosed;
      await commentCloseAPI.close(id, newState);
      setCommentsClosed(newState);
      toast.success(newState ? 'Comments closed' : 'Comments opened');
    } catch { toast.error('Failed to update'); }
  };

  const buildSaveData = (status) => {
    const data = { ...form, status: status || form.status };
    // Parse custom_meta JSON
    if (data.custom_meta && typeof data.custom_meta === 'string') {
      try { data.custom_meta = JSON.parse(data.custom_meta); } catch { data.custom_meta = null; }
    }
    if (!data.custom_meta || typeof data.custom_meta !== 'object') data.custom_meta = null;
    // Normalize location (empty → null)
    if (!data.location?.name && !data.location?.country && !data.location?.lat && !data.location?.lon) {
      data.location = null;
    } else {
      const loc = { ...data.location };
      if (loc.lat) loc.lat = parseFloat(loc.lat);
      if (loc.lon) loc.lon = parseFloat(loc.lon);
      data.location = loc;
    }
    // story_type empty → null
    if (!data.story_type) data.story_type = null;
    // series_id empty → null
    if (!data.series_id) data.series_id = null;
    // expires_at empty → null
    if (!data.expires_at) data.expires_at = null;
    // scheduled_at empty → null
    if (!data.scheduled_at) data.scheduled_at = null;
    // entities — pass as-is (array)
    if (!Array.isArray(data.entities)) data.entities = [];
    // Empty featured_image / video_url → null
    if (!data.featured_image) data.featured_image = null;
    if (!data.video_url) data.video_url = null;
    // Empty OG/canonical → null
    if (!data.canonical_url) data.canonical_url = null;
    if (!data.og_title) data.og_title = null;
    if (!data.og_description) data.og_description = null;
    if (!data.og_image) data.og_image = null;
    return data;
  };

  const handleSave = async (status) => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const data = buildSaveData(status);
      if (editorMode === 'blocks' && blocks.length) {
        data.has_blocks = true;
      }
      if (isEdit) {
        await versionsAPI.save(id).catch(() => {});
        await articlesAPI.update(id, data);
        if (editorMode === 'blocks' && blocks.length) {
          await blocksAPI.save(id, { blocks }).catch(() => {});
        }
        toast.success('Article updated!');
        loadArticle();
      } else {
        const created = await articlesAPI.create(data);
        const newId = created.data?.id || created.data?._id;
        if (newId && editorMode === 'blocks' && blocks.length) {
          await blocksAPI.save(newId, { blocks }).catch(() => {});
        }
        toast.success('Article created!');
        navigate('/admin/articles');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg || d).join('; ') : (typeof detail === 'string' ? detail : 'Failed to save');
      toast.error(msg);
      console.error('[Publish Error]', err.response?.data || err);
    } finally { setSaving(false); }
  };

  const handleWorkflowAction = async () => {
    if (!workflowAction) return;
    try {
      await workflowAPI.action(id, {
        action: workflowAction,
        comment: workflowComment || null,
        scheduled_at: workflowAction === 'schedule' ? form.scheduled_at : null
      });
      toast.success(`Article ${workflowAction.replace('_', ' ')}!`);
      setShowWorkflowDialog(false);
      setWorkflowComment('');
      setWorkflowAction('');
      loadArticle();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Workflow action failed');
    }
  };

  const handleSetPriority = async (priority) => {
    try {
      await workflowAPI.setPriority(id, { priority });
      setArticlePriority(priority);
      toast.success(`Priority set to ${priority}`);
    } catch { toast.error('Failed'); }
  };

  const handleRollback = async (version) => {
    try {
      await versionsAPI.rollback(id, version);
      toast.success(`Rolled back to version ${version}`);
      loadArticle();
      setShowVersions(false);
    } catch { toast.error('Rollback failed'); }
  };

  const addTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      setForm({ ...form, tags: [...form.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) return;
    setAiLoading(true);
    try {
      const res = await aiAPI.generate({
        prompt: aiPrompt, tone: 'professional', length: 'medium',
        category: categories.find(c => c.id === form.category_id)?.name || null
      });
      const result = res.data.result;
      setForm(prev => ({
        ...prev,
        title: result.title || prev.title,
        subtitle: result.subtitle || prev.subtitle,
        content: result.content || prev.content,
        tags: result.tags || prev.tags,
      }));
      toast.success('AI content generated!');
    } catch { toast.error('AI generation failed'); }
    finally { setAiLoading(false); }
  };

  const handleAISummarize = async () => {
    if (!form.content) { toast.error('No content'); return; }
    setAiLoading(true);
    try {
      const res = await aiAPI.summarize({ content: form.content, style: 'brief' });
      setForm(prev => ({
        ...prev,
        subtitle: res.data.result?.summary || prev.subtitle,
        tags: res.data.result?.suggested_tags || prev.tags,
      }));
      toast.success('Summarized!');
    } catch { toast.error('Failed'); }
    finally { setAiLoading(false); }
  };

  const handlePreflight = async () => {
    setPreflightLoading(true);
    try {
      const res = await preflightAPI.check(id);
      setPreflightResult(res.data);
      const passing = res.data.checks?.filter(c => c.passed).length || 0;
      const failing = res.data.checks?.filter(c => !c.passed).length || 0;
      if (failing === 0) toast.success(`Pre-flight passed! All ${passing} checks OK`);
      else toast.warning(`Pre-flight: ${passing} passed, ${failing} failed`);
    } catch { toast.error('Pre-flight check failed'); }
    finally { setPreflightLoading(false); }
  };

  const handleAutoTag = async () => {
    if (!form.content) { toast.error('No content'); return; }
    setAutoTagLoading(true);
    try {
      const res = await aiToolsAPI.autoTag({ content: form.content });
      const tags = res.data.tags || [];
      if (tags.length > 0) {
        setForm(prev => ({ ...prev, tags: [...new Set([...prev.tags, ...tags])] }));
        toast.success(`${tags.length} tags suggested`);
      } else {
        toast.info('No tags suggested');
      }
    } catch { toast.error('Auto-tag failed'); }
    finally { setAutoTagLoading(false); }
  };

  const handleAltText = async () => {
    if (!form.featured_image) { toast.error('Add a featured image first'); return; }
    try {
      const r = await aiV7API.altText({ image_url: form.featured_image, context: form.title });
      const alt = r.data?.alt_text || '';
      if (alt) {
        setForm((prev) => ({ ...prev, og_image: prev.og_image || form.featured_image, custom_meta: prev.custom_meta }));
        toast.success(`Alt-text: ${alt}`);
        navigator.clipboard?.writeText(alt).catch(() => {});
      }
    } catch { toast.error('Alt-text generation failed'); }
  };

  const handleHeadlineExperiment = async () => {
    if (!isEdit) { toast.error('Save the article first'); return; }
    const variantsRaw = window.prompt('Enter 2-4 alternative headlines, one per line:', form.title);
    if (!variantsRaw) return;
    const variants = variantsRaw.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    if (variants.length < 2) { toast.error('Provide at least 2 variants'); return; }
    try {
      await headlineExpAPI.create(id, variants);
      toast.success(`Headline experiment with ${variants.length} variants started`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to start experiment'); }
  };

  const handlePersistedAutoTag = async () => {
    if (!isEdit) { handleAutoTag(); return; }
    try {
      const r = await aiV7API.autoTag(id);
      setForm((prev) => ({ ...prev, tags: r.data?.tags || prev.tags }));
      toast.success('Tags + categorisation refreshed');
    } catch { toast.error('Auto-tag failed'); }
  };

  const handleSEOGen = async () => {
    if (!form.title) { toast.error('Title required'); return; }
    setSeoGenLoading(true);
    try {
      const res = await aiToolsAPI.generateSEO({ title: form.title, content: form.content });
      const result = res.data;
      setSeoMeta({ title: result.meta_title || '', description: result.meta_description || '' });
      toast.success('SEO metadata generated');
    } catch { toast.error('SEO gen failed'); }
    finally { setSeoGenLoading(false); }
  };

  const handleSetSlug = async () => {
    if (!slug.trim()) return;
    try {
      await slugsAPI.set(id, slug.trim());
      toast.success('Custom slug set');
    } catch { toast.error('Failed to set slug'); }
  };

  // ── Headline assistant ────────────────────────────────────────────────
  // Inline popover next to the title input. Offers 5 alternatives tuned
  // for the current draft with a one-click "Use" action.
  const handleHeadlineSuggest = async () => {
    setHeadlineOpen(true);
    if (!form.title && !form.content) {
      setHeadlineSuggestions([]);
      return;
    }
    setHeadlineLoading(true);
    try {
      const res = await aiExtrasAPI.headlines({
        title: form.title,
        content: form.content,
        tone: 'neutral',
        provider: aiModel.provider,
        model: aiModel.model,
      });
      setHeadlineSuggestions(res.data.headlines || []);
    } catch {
      toast.error('Headline suggestions unavailable');
      setHeadlineSuggestions([]);
    } finally {
      setHeadlineLoading(false);
    }
  };

  const applyHeadline = (h) => {
    setForm((prev) => ({ ...prev, title: h }));
    setHeadlineOpen(false);
    toast.success('Headline replaced — remember to save');
  };

  // ── Social post generator ─────────────────────────────────────────────
  // Wraps the existing /ai/social-post endpoint. Opens a dialog with
  // per-platform copy + copy-to-clipboard so the social editor can
  // queue posts without leaving the article screen.
  const handleSocialPosts = async () => {
    if (!form.title) { toast.error('Title required'); return; }
    setSocialOpen(true);
    setSocialLoading(true);
    try {
      const res = await aiExtrasAPI.socialPost({
        title: form.title,
        content: form.content,
        platforms: ['twitter', 'linkedin', 'facebook', 'instagram'],
        url: '', // article URL gets resolved downstream; keep empty for drafts
        provider: aiModel.provider,
        model: aiModel.model,
      });
      setSocialPosts(res.data.posts || {});
    } catch {
      toast.error('Could not generate social posts');
      setSocialPosts({});
    } finally {
      setSocialLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Clipboard unavailable');
    }
  };

  // ── Smart scheduler ───────────────────────────────────────────────────
  // Convert a set of quick-pick presets into an ISO datetime string and
  // drop it on the form. The existing workflow ``schedule`` action picks
  // it up unchanged. Times are resolved in the user's local zone.
  const applySchedulePreset = (preset) => {
    const now = new Date();
    const d = new Date(now);
    switch (preset) {
      case 'in_1h':
        d.setHours(d.getHours() + 1);
        break;
      case 'tonight_6pm':
        d.setHours(18, 0, 0, 0);
        if (d <= now) d.setDate(d.getDate() + 1);
        break;
      case 'tomorrow_8am':
        d.setDate(d.getDate() + 1);
        d.setHours(8, 0, 0, 0);
        break;
      case 'monday_9am': {
        const delta = (8 - d.getDay()) % 7 || 7; // next Monday
        d.setDate(d.getDate() + delta);
        d.setHours(9, 0, 0, 0);
        break;
      }
      default:
        return;
    }
    // datetime-local expects yyyy-MM-ddTHH:mm with no timezone suffix.
    const pad = (n) => String(n).padStart(2, '0');
    const iso = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setForm((prev) => ({ ...prev, scheduled_at: iso }));
    toast.success(`Scheduled for ${d.toLocaleString()}`);
  };

  const role = user?.role || 'reader';
  const isEditor = ['super_admin', 'admin', 'editor'].includes(role);
  const status = form.status;
  const statusInfo = STATUS_CONFIG[status] || STATUS_CONFIG.draft;

  // Available workflow actions based on current status and role
  const getAvailableActions = () => {
    const actions = [];
    if (status === 'draft' && ['super_admin', 'admin', 'editor', 'journalist'].includes(role))
      actions.push({ action: 'submit', label: 'Submit for Review', icon: Send, color: '#1E5A8A' });
    if (status === 'in_review' && isEditor) {
      actions.push({ action: 'sub_edit', label: 'Mark Sub-Edited', icon: FileText, color: '#1E5A8A' });
      actions.push({ action: 'approve', label: 'Approve', icon: CheckCircle, color: '#1F7A5A' });
      actions.push({ action: 'request_changes', label: 'Request Changes', icon: RotateCcw, color: '#9A6B1A' });
    }
    if (status === 'sub_edited' && isEditor) {
      actions.push({ action: 'legal_review', label: 'Send to Legal', icon: Shield, color: '#4A1D2A' });
      actions.push({ action: 'approve', label: 'Approve', icon: CheckCircle, color: '#1F7A5A' });
    }
    if (status === 'legal_review' && isEditor) {
      actions.push({ action: 'approve', label: 'Approve', icon: CheckCircle, color: '#1F7A5A' });
      actions.push({ action: 'request_changes', label: 'Request Changes', icon: RotateCcw, color: '#9A6B1A' });
    }
    if (status === 'approved' && isEditor) {
      actions.push({ action: 'publish', label: 'Publish Now', icon: CheckCircle, color: '#1F7A5A' });
      actions.push({ action: 'schedule', label: 'Schedule', icon: Clock, color: '#B89B5E' });
    }
    if (['published', 'scheduled', 'approved'].includes(status) && ['super_admin', 'admin'].includes(role))
      actions.push({ action: 'kill', label: 'Kill Article', icon: Skull, color: '#B3261E' });
    if (['published', 'killed'].includes(status) && isEditor)
      actions.push({ action: 'archive', label: 'Archive', icon: XCircle, color: '#5B616E' });
    if (['archived', 'killed'].includes(status) && ['super_admin', 'admin'].includes(role))
      actions.push({ action: 'reopen', label: 'Reopen as Draft', icon: RotateCcw, color: '#1E5A8A' });
    // Also allow direct review for backward compat
    if (status === 'review' && isEditor)
      actions.push({ action: 'approve', label: 'Approve', icon: CheckCircle, color: '#1F7A5A' });
    return actions;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const availableActions = getAvailableActions();

  return (
    <div className="space-y-6 max-w-6xl">
      {/* Draft recovery banner */}
      {draftRecovery && (
        <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <p className="text-sm text-amber-800">
            <AlertTriangle className="w-4 h-4 inline mr-1.5 text-amber-600" />
            Unsaved draft from {draftRecovery.autosaved_at ? new Date(draftRecovery.autosaved_at).toLocaleString() : 'earlier'}
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="text-xs border-amber-300 text-amber-700 hover:bg-amber-100"
              onClick={() => {
                setForm(f => ({ ...f, title: draftRecovery.autosave_title || f.title, content: draftRecovery.autosave_content }));
                setDraftRecovery(null);
                toast.success('Draft restored');
              }}>
              Restore draft
            </Button>
            <Button size="sm" variant="ghost" className="text-xs text-amber-600" onClick={() => setDraftRecovery(null)}>Dismiss</Button>
          </div>
        </div>
      )}

      {/* Presence indicators + live CRDT activity */}
      {presentUsers.filter(u => u.user_id !== (user?.id || user?.sub)).length > 0 && (
        <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-xl px-4 py-2">
          <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
          <span className="text-xs text-blue-600 font-medium">Co-editing:</span>
          {presentUsers.filter(u => u.user_id !== (user?.id || user?.sub)).map(u => (
            <span key={u.user_id} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{u.user_name || u.user_email}</span>
          ))}
          {remoteOps.length > 0 && (
            <span className="text-xs text-blue-500 ml-2">
              ✏️ {remoteOps[remoteOps.length - 1]?.user_email?.split('@')[0]} is editing…
            </span>
          )}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => { if (isEdit) versionsAPI.unlock(id).catch(() => {}); navigate('/admin/articles'); }} className="p-2 hover:bg-[#E7E0D6] rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">{isEdit ? 'Edit Article' : 'New Article'}</h1>
              {isEdit && (
                <Badge data-testid="article-status-badge" className="text-[10px]" style={{ background: `${statusInfo.color}15`, color: statusInfo.color }}>
                  {statusInfo.label}
                </Badge>
              )}
              {isEdit && articlePriority !== 'standard' && (
                <Badge className="text-[10px]" style={{ background: `${PRIORITY_CONFIG[articlePriority]?.color}15`, color: PRIORITY_CONFIG[articlePriority]?.color }}>
                  <Flag className="w-3 h-3 mr-1" />{PRIORITY_CONFIG[articlePriority]?.label}
                </Badge>
              )}
              {isLocked && <Badge variant="outline" className="text-[10px] text-[#B3261E]"><Lock className="w-3 h-3 mr-1" /> Locked</Badge>}
            </div>
            <p className="text-sm text-[#5B616E] mt-0.5">
              {isEdit ? `${versions.length} version${versions.length !== 1 ? 's' : ''} saved` : 'Write and publish your content'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {isEdit && (
            <Button variant="outline" size="sm" onClick={() => setShowVersions(true)} data-testid="show-versions-btn">
              <History className="w-4 h-4 mr-1" /> Versions
            </Button>
          )}
          <Button variant="outline" onClick={() => handleSave('draft')} disabled={saving}>
            <Save className="w-4 h-4 mr-2" /> Save Draft
          </Button>
          {isEditor && (
            <Button className="bg-[#121316] text-white hover:bg-[#1B1D22]" onClick={() => handleSave('published')} disabled={saving}>
              Publish
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-4">
          <div className="relative">
            <input
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder="Article headline..."
              className="w-full text-2xl font-bold news-masthead border-0 border-b border-[#E7E0D6] bg-transparent py-3 pr-28 focus:outline-none focus:border-[#B89B5E] placeholder:text-[#E7E0D6]"
              data-testid="article-title-input"
            />
            {/* Inline AI headline assistant — calls /api/ai/headlines with the
                current draft and proposes 5 alternatives with rationale. */}
            <button
              type="button"
              onClick={handleHeadlineSuggest}
              disabled={headlineLoading || (!form.title && !form.content)}
              className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-1 text-[10px] font-semibold text-[#B89B5E] hover:text-[#8E7530] disabled:text-[#E7E0D6] disabled:cursor-not-allowed border border-[#E7E0D6] rounded-full px-2.5 py-1 bg-white/90 backdrop-blur"
              title="Suggest alternative headlines"
            >
              {headlineLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              Suggest
            </button>
          </div>
          <input
            value={form.subtitle}
            onChange={e => setForm({ ...form, subtitle: e.target.value })}
            placeholder="Subtitle or deck..."
            className="w-full text-base border-0 border-b border-[#E7E0D6] bg-transparent py-2 focus:outline-none focus:border-[#B89B5E] placeholder:text-[#E7E0D6] text-[#5B616E]"
          />
          <div className="flex items-center gap-2 mt-2 mb-1">
            <button onClick={() => setEditorMode('markdown')} className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${editorMode === 'markdown' ? 'bg-[#121316] text-white' : 'text-[#5B616E] hover:bg-[#E7E0D6]'}`}>Markdown</button>
            <button onClick={() => setEditorMode('blocks')} className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${editorMode === 'blocks' ? 'bg-[#121316] text-white' : 'text-[#5B616E] hover:bg-[#E7E0D6]'}`}>Block Editor</button>
          </div>
          {editorMode === 'markdown' ? (
            <InlineSelectionToolbar>
              <div data-color-mode="light">
                <MDEditor
                  value={form.content}
                  onChange={val => setForm({ ...form, content: val || '' })}
                  preview="live"
                  height={400}
                  textareaProps={{ placeholder: "Write your article content... (supports Markdown). Highlight text in the preview pane to add an annotation.", 'data-testid': 'article-content-editor' }}
                />
              </div>
            </InlineSelectionToolbar>
          ) : (
            <InlineSelectionToolbar>
              <BlockEditor blocks={blocks} onChange={setBlocks} />
            </InlineSelectionToolbar>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Tabs value={sidebarTab} onValueChange={setSidebarTab}>
            <TabsList className="w-full flex-wrap h-auto gap-0.5">
              <TabsTrigger value="publish" className="flex-1 text-xs">Publish</TabsTrigger>
              <TabsTrigger value="workflow" className="flex-1 text-xs">Workflow</TabsTrigger>
              <TabsTrigger value="media" className="flex-1 text-xs">Media</TabsTrigger>
              <TabsTrigger value="meta" className="flex-1 text-xs">Meta</TabsTrigger>
              <TabsTrigger value="ai" className="flex-1 text-xs">AI</TabsTrigger>
            </TabsList>

            {/* PUBLISH TAB */}
            <TabsContent value="publish" className="space-y-4 mt-3">
              {/* Category */}
              <Card className="bg-white border-[#E7E0D6]">
                <CardContent className="pt-4 space-y-3">
                  <div>
                    <label className="text-xs text-[#5B616E] block mb-1">Category</label>
                    <Select value={form.category_id} onValueChange={v => setForm({ ...form, category_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                      <SelectContent>{categories.map(cat => <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-[#5B616E] block mb-1">Edition / region</label>
                    <Select value={form.edition || 'global'} onValueChange={(v) => setForm({ ...form, edition: v })}>
                      <SelectTrigger data-testid="article-edition-select">
                        <SelectValue placeholder="Edition" />
                      </SelectTrigger>
                      <SelectContent>
                        {editionOptions.map((ed) => (
                          <SelectItem key={ed.slug || ed.id} value={ed.slug || ed.id}>
                            {ed.name || ed.slug}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-[#5B616E] mt-1">
                      International shows in every edition; regional editions filter the public homepage and widgets.
                    </p>
                  </div>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                      <input type="checkbox" checked={form.is_featured} onChange={e => setForm({ ...form, is_featured: e.target.checked })} className="rounded" /> Featured
                    </label>
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                      <input type="checkbox" checked={form.is_trending} onChange={e => setForm({ ...form, is_trending: e.target.checked })} className="rounded" /> Trending
                    </label>
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                      <input type="checkbox" checked={form.is_premium} onChange={e => setForm({ ...form, is_premium: e.target.checked })} className="rounded" /> Premium
                    </label>
                  </div>
                </CardContent>
              </Card>

              {/* Tags */}
              <Card className="bg-white border-[#E7E0D6]">
                <CardContent className="pt-4">
                  <label className="text-xs text-[#5B616E] block mb-1 flex items-center gap-1"><Tag className="w-3 h-3" /> Tags</label>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {form.tags.map(tag => (
                      <Badge key={tag} variant="secondary" className="text-[10px] cursor-pointer" onClick={() => setForm({ ...form, tags: form.tags.filter(t => t !== tag) })}>
                        {tag} &times;
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())} placeholder="Add tag..." className="flex-1 border border-[#E7E0D6] rounded-lg px-3 py-1.5 text-xs bg-white" />
                    <Button size="sm" variant="outline" onClick={addTag} className="text-xs">Add</Button>
                  </div>
                </CardContent>
              </Card>

              {/* Priority (edit only) */}
              {isEdit && (
                <Card className="bg-white border-[#E7E0D6]">
                  <CardContent className="pt-4">
                    <label className="text-xs text-[#5B616E] block mb-1 flex items-center gap-1"><Flag className="w-3 h-3" /> Priority</label>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(PRIORITY_CONFIG).map(([key, cfg]) => (
                        <button key={key} onClick={() => handleSetPriority(key)}
                          className={`px-2.5 py-1 rounded-lg text-[10px] font-medium border transition-colors ${articlePriority === key ? 'text-white' : 'text-[#5B616E] border-[#E7E0D6]'}`}
                          style={articlePriority === key ? { background: cfg.color, borderColor: cfg.color } : {}}
                        >{cfg.label}</button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Schedule */}
              <Card className="bg-white border-[#E7E0D6]">
                <CardContent className="pt-4 space-y-2">
                  <label className="text-xs text-[#5B616E] block flex items-center gap-1"><Clock className="w-3 h-3" /> Schedule Publication</label>
                  <Input type="datetime-local" value={form.scheduled_at?.slice(0, 16) || ''} onChange={e => setForm({ ...form, scheduled_at: e.target.value })} className="text-xs" />
                  {/* Quick-pick presets — resolve to the user's local
                      timezone and write an ISO string into scheduled_at. */}
                  <div className="grid grid-cols-2 gap-1.5 pt-1">
                    <Button type="button" size="sm" variant="outline" className="text-[10px] h-7" onClick={() => applySchedulePreset('in_1h')}>In 1 hour</Button>
                    <Button type="button" size="sm" variant="outline" className="text-[10px] h-7" onClick={() => applySchedulePreset('tonight_6pm')}>Tonight · 6pm</Button>
                    <Button type="button" size="sm" variant="outline" className="text-[10px] h-7" onClick={() => applySchedulePreset('tomorrow_8am')}>Tomorrow · 8am</Button>
                    <Button type="button" size="sm" variant="outline" className="text-[10px] h-7" onClick={() => applySchedulePreset('monday_9am')}>Next Monday · 9am</Button>
                  </div>
                  {form.scheduled_at && (
                    <p className="text-[10px] text-[#5B616E]">
                      Publishes {new Date(form.scheduled_at).toLocaleString()}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Clone + Comment Close */}
              {isEdit && (
                <Card className="bg-white border-[#E7E0D6]">
                  <CardContent className="pt-4 space-y-2">
                    <Button variant="outline" size="sm" className="w-full text-xs" onClick={handleClone}>
                      <Copy className="w-3 h-3 mr-1" /> Clone Article
                    </Button>
                    <Button variant={commentsClosed ? 'default' : 'outline'} size="sm" className={`w-full text-xs ${commentsClosed ? 'bg-[#B3261E] text-white hover:bg-[#9E1F19]' : ''}`} onClick={handleToggleComments}>
                      <MessageSquareOff className="w-3 h-3 mr-1" /> {commentsClosed ? 'Comments Closed' : 'Close Comments'}
                    </Button>
                    {lastAutosave && (
                      <p className="text-[10px] text-[#5B616E] text-center">Auto-saved {lastAutosave.toLocaleTimeString()}</p>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* WORKFLOW TAB */}
            <TabsContent value="workflow" className="space-y-4 mt-3">
              {isEdit ? (
                <>
                  <Card className="bg-white border-[#E7E0D6]">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-xs flex items-center gap-2"><GitBranch className="w-3 h-3" /> Workflow Pipeline</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {/* Stage indicator */}
                      <div className="flex items-center gap-1 overflow-x-auto pb-1">
                        {['draft', 'in_review', 'sub_edited', 'legal_review', 'approved', 'published'].map((stage, i) => {
                          const cfg = STATUS_CONFIG[stage];
                          const isCurrent = status === stage || (status === 'review' && stage === 'in_review');
                          const isPast = ['draft', 'in_review', 'sub_edited', 'legal_review', 'approved', 'published'].indexOf(status) > i;
                          return (
                            <React.Fragment key={stage}>
                              {i > 0 && <div className={`w-4 h-px ${isPast || isCurrent ? 'bg-[#1F7A5A]' : 'bg-[#E7E0D6]'}`} />}
                              <div className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[9px] font-medium ${isCurrent ? 'text-white' : isPast ? 'text-[#1F7A5A] bg-[#1F7A5A]/10' : 'text-[#5B616E] bg-[#E7E0D6]'}`}
                                style={isCurrent ? { background: cfg.color } : {}}>
                                {cfg.label}
                              </div>
                            </React.Fragment>
                          );
                        })}
                      </div>

                      {/* Available actions */}
                      {availableActions.length > 0 && (
                        <div className="flex flex-col gap-1.5 pt-2 border-t border-[#E7E0D6]">
                          {availableActions.map(({ action, label, icon: Icon, color }) => (
                            <Button key={action} size="sm" variant="outline" className="w-full justify-start text-xs"
                              style={{ borderColor: `${color}30`, color }}
                              onClick={() => { setWorkflowAction(action); setShowWorkflowDialog(true); }}>
                              <Icon className="w-3 h-3 mr-2" /> {label}
                            </Button>
                          ))}
                        </div>
                      )}

                      {/* Per-article SLA timer */}
                      <SlaTimer
                        article={{
                          status: form.status,
                          updated_at: form.scheduled_at || form.expires_at || (workflowHistory.at(-1)?.timestamp),
                          created_at: undefined,
                        }}
                        slaHours={24}
                      />
                    </CardContent>
                  </Card>

                  {/* Approval chain (parallel + sequential) */}
                  {isEdit && (
                    <Card className="bg-white border-[#E7E0D6]">
                      <CardHeader className="pb-2"><CardTitle className="text-xs flex items-center gap-1"><GitBranch className="w-3 h-3" /> Approval Chain</CardTitle></CardHeader>
                      <CardContent>
                        <ApprovalChain articleId={id} currentUserRole={user?.role} />
                      </CardContent>
                    </Card>
                  )}

                  {/* Inline annotations */}
                  {isEdit && (
                    <Card className="bg-white border-[#E7E0D6]">
                      <CardHeader className="pb-2"><CardTitle className="text-xs flex items-center gap-1"><MessageCircle className="w-3 h-3" /> Annotations</CardTitle></CardHeader>
                      <CardContent>
                        <AnnotationsPanel articleId={id} />
                      </CardContent>
                    </Card>
                  )}

                  {/* Parallel peer approvals */}
                  {isEdit && ['in_review','sub_edited','legal_review'].includes(form.status) && (
                    <Card className="bg-white border-[#E7E0D6]">
                      <CardHeader className="pb-2"><CardTitle className="text-xs flex items-center gap-1"><Shield className="w-3 h-3" /> Peer Approvals ({articleApprovals.length}/{requiredApprovals})</CardTitle></CardHeader>
                      <CardContent className="space-y-2">
                        <div className="flex flex-wrap gap-1 mb-1">
                          {articleApprovals.map((a, i) => (
                            <span key={i} className="text-[10px] bg-green-50 text-green-700 border border-green-200 rounded-full px-2 py-0.5">{a.user_email || a.user_id}</span>
                          ))}
                          {articleApprovals.length === 0 && <span className="text-[10px] text-[#5B616E]">No approvals yet</span>}
                        </div>
                        <div className="flex items-center gap-1 mb-1">
                          <span className="text-[10px] text-[#5B616E]">Required:</span>
                          <input type="number" min={1} max={10} value={requiredApprovals} onChange={e => setRequiredApprovals(parseInt(e.target.value, 10) || 1)} className="w-14 border border-[#E7E0D6] rounded px-2 py-0.5 text-xs" />
                          <Button size="sm" variant="outline" className="text-xs" onClick={async () => { await peerApproveAPI.setRequired(id, requiredApprovals); toast.success('Required approvals updated'); }}>Set</Button>
                        </div>
                        {!articleApprovals.some(a => a.user_id === (user?.id || user?.sub)) && (
                          <>
                            <Input className="text-xs" placeholder="Optional note..." value={approvalNote} onChange={e => setApprovalNote(e.target.value)} />
                            <Button size="sm" className="w-full text-xs bg-[#1F7A5A] text-white hover:bg-[#1A6B4E]" onClick={handlePeerApprove} disabled={approvingLoading}>
                              {approvingLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                              Submit My Approval
                            </Button>
                          </>
                        )}
                        {articleApprovals.some(a => a.user_id === (user?.id || user?.sub)) && (
                          <p className="text-[10px] text-green-600 text-center">✓ You have already approved this article</p>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* History */}
                  {workflowHistory.length > 0 && (
                    <Card className="bg-white border-[#E7E0D6]">
                      <CardHeader className="pb-2"><CardTitle className="text-xs">Workflow History</CardTitle></CardHeader>
                      <CardContent>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {workflowHistory.slice().reverse().map((h, i) => (
                            <div key={i} className="text-[10px] text-[#5B616E] flex gap-2">
                              <div className="w-1.5 h-1.5 rounded-full bg-[#B89B5E] mt-1.5 flex-shrink-0" />
                              <div>
                                <span className="font-medium text-[#121316]">{h.action?.replace('_', ' ')}</span>
                                <span className="mx-1">&middot;</span>
                                <span>{h.user_email || 'system'}</span>
                                {h.comment && <p className="italic mt-0.5 text-[9px] pl-2 border-l border-[#E7E0D6]">{h.comment}</p>}
                                <p className="text-[9px] mt-0.5">{h.timestamp ? new Date(h.timestamp).toLocaleString() : ''}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                <Card className="bg-white border-[#E7E0D6]">
                  <CardContent className="pt-6 text-center text-sm text-[#5B616E]">
                    <GitBranch className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p>Save the article first to access workflow controls</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* MEDIA TAB */}
            <TabsContent value="media" className="space-y-4 mt-3">
              {isEdit && (
                <ChartBlockBuilder
                  articleId={id}
                  onInserted={(viz) => toast.success(`Chart "${viz.title}" inserted`)}
                />
              )}
              <Card className="bg-white border-[#E7E0D6]">
                <CardContent className="pt-4 space-y-3">
                  <div>
                    <label className="text-xs text-[#5B616E] block mb-1">Featured Image URL</label>
                    <input value={form.featured_image} onChange={e => setForm({ ...form, featured_image: e.target.value })} placeholder="https://..." className="w-full border border-[#E7E0D6] rounded-lg px-3 py-1.5 text-xs bg-white" />
                    {form.featured_image && <img src={form.featured_image} alt="preview" className="mt-2 rounded-lg w-full h-32 object-cover" />}
                  </div>
                  <div>
                    <label className="text-xs text-[#5B616E] block mb-1">Video URL</label>
                    <input value={form.video_url} onChange={e => setForm({ ...form, video_url: e.target.value })} placeholder="https://youtube.com/..." className="w-full border border-[#E7E0D6] rounded-lg px-3 py-1.5 text-xs bg-white" />
                  </div>
                </CardContent>
              </Card>

              {/* SEO */}
              <Card className="bg-white border-[#E7E0D6]">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xs flex items-center gap-1"><Search className="w-3 h-3" /> SEO</CardTitle>
                    <Button size="sm" variant="ghost" className="text-[10px] h-6 px-2" onClick={handleSEOGen} disabled={seoGenLoading}>
                      {seoGenLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />} AI Generate
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div><label className="text-[10px] text-[#5B616E] block mb-0.5">Meta Title</label><Input className="text-xs" value={seoMeta.title} onChange={e => setSeoMeta({...seoMeta, title: e.target.value})} placeholder="Custom meta title..." /></div>
                  <div><label className="text-[10px] text-[#5B616E] block mb-0.5">Meta Description</label><Textarea className="text-xs" rows={2} value={seoMeta.description} onChange={e => setSeoMeta({...seoMeta, description: e.target.value})} placeholder="Custom meta description..." /></div>
                  {isEdit && (
                    <div>
                      <label className="text-[10px] text-[#5B616E] block mb-0.5 flex items-center gap-1"><Link2 className="w-3 h-3" /> Custom Slug</label>
                      <div className="flex gap-1">
                        <Input className="text-xs flex-1" value={slug} onChange={e => setSlug(e.target.value)} placeholder="my-article-slug" />
                        <Button size="sm" variant="outline" className="text-xs" onClick={handleSetSlug}>Set</Button>
                      </div>
                    </div>
                  )}
                  <div className="border-t border-[#E7E0D6] pt-2 mt-1">
                    <p className="text-[10px] text-[#5B616E] font-semibold mb-1.5">Open Graph / Canonical overrides</p>
                    <div className="space-y-1.5">
                      <div><label className="text-[10px] text-[#5B616E] block mb-0.5">Canonical URL</label><Input className="text-xs" value={form.canonical_url || ''} onChange={e => setForm({...form, canonical_url: e.target.value})} placeholder="https://..." /></div>
                      <div><label className="text-[10px] text-[#5B616E] block mb-0.5">OG Title</label><Input className="text-xs" value={form.og_title || ''} onChange={e => setForm({...form, og_title: e.target.value})} placeholder="Auto from headline" /></div>
                      <div><label className="text-[10px] text-[#5B616E] block mb-0.5">OG Description</label><Textarea className="text-xs" rows={2} value={form.og_description || ''} onChange={e => setForm({...form, og_description: e.target.value})} placeholder="Auto from subtitle/content" /></div>
                      <div><label className="text-[10px] text-[#5B616E] block mb-0.5">OG Image URL</label><Input className="text-xs" value={form.og_image || ''} onChange={e => setForm({...form, og_image: e.target.value})} placeholder="Auto from featured image" /></div>
                    </div>
                    {/* OG/Twitter Card Live Preview */}
                    {(form.og_title || form.title) && (
                      <div className="mt-2 border border-[#E7E0D6] rounded-xl overflow-hidden">
                        <p className="text-[9px] text-[#5B616E] px-2 pt-1.5 uppercase tracking-wide">Social card preview</p>
                        {(form.og_image || form.featured_image) && (
                          <img src={form.og_image || form.featured_image} alt="OG preview" className="w-full h-28 object-cover" />
                        )}
                        <div className="p-2.5 bg-[#F0F2F5]">
                          <p className="text-[9px] text-[#65676B] uppercase">{typeof window !== 'undefined' ? window.location.hostname : 'neuralnews.com'}</p>
                          <p className="text-sm font-semibold text-[#1C1E21] leading-snug line-clamp-2">{form.og_title || form.title}</p>
                          <p className="text-xs text-[#65676B] line-clamp-2 mt-0.5">{form.og_description || form.subtitle || ''}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Pre-flight */}
              {isEdit && (
                <Card className="bg-white border-[#E7E0D6]">
                  <CardContent className="pt-4 space-y-2">
                    <Button variant="outline" size="sm" className="w-full text-xs" onClick={handlePreflight} disabled={preflightLoading}>
                      {preflightLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Rocket className="w-3 h-3 mr-1" />}
                      Run Pre-flight Checks
                    </Button>
                    {preflightResult && (
                      <div className="space-y-1 mt-2">
                        {preflightResult.checks?.map((chk, i) => (
                          <div key={i} className={`flex items-center gap-2 text-[10px] p-1.5 rounded ${chk.passed ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                            {chk.passed ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                            {chk.label || chk.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* META TAB — §1 taxonomy & metadata */}
            <TabsContent value="meta" className="space-y-4 mt-3">
              {/* Story type */}
              <Card className="bg-white border-[#E7E0D6]">
                <CardContent className="pt-4 space-y-3">
                  <div>
                    <label className="text-xs text-[#5B616E] block mb-1 flex items-center gap-1"><FileText className="w-3 h-3" /> Story type</label>
                    <Select value={form.story_type || ''} onValueChange={v => setForm({ ...form, story_type: v })}>
                      <SelectTrigger data-testid="story-type-select">
                        <SelectValue placeholder="Select story type..." />
                      </SelectTrigger>
                      <SelectContent>
                        {['news', 'feature', 'opinion', 'explainer', 'interview', 'review', 'live', 'gallery', 'longform', 'analysis', 'data'].map(t => (
                          <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Series */}
                  <div>
                    <label className="text-xs text-[#5B616E] block mb-1 flex items-center gap-1"><Layers className="w-3 h-3" /> Series</label>
                    <Select value={form.series_id || 'none'} onValueChange={v => setForm({ ...form, series_id: v === 'none' ? '' : v })}>
                      <SelectTrigger data-testid="series-select">
                        <SelectValue placeholder="No series" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— No series —</SelectItem>
                        {seriesList.map(s => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Language */}
                  <div>
                    <label className="text-xs text-[#5B616E] block mb-1">Language</label>
                    <Select value={form.language || 'en'} onValueChange={v => setForm({ ...form, language: v })}>
                      <SelectTrigger className="text-xs" data-testid="article-language-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {languages.length > 0 ? languages.map(l => <SelectItem key={l.code} value={l.code}>{l.name} ({l.code})</SelectItem>) : <SelectItem value="en">English (en)</SelectItem>}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Content expiry */}
                  <div>
                    <label className="text-xs text-[#5B616E] block mb-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Content expiry (auto-archive)</label>
                    <Input type="datetime-local" value={form.expires_at || ''} onChange={e => setForm({ ...form, expires_at: e.target.value })} className="text-xs" />
                    <p className="text-[10px] text-[#5B616E] mt-0.5">Article will be auto-archived after this date (via expiry check).</p>
                  </div>
                </CardContent>
              </Card>

              {/* Entity tagging */}
              <Card className="bg-white border-[#E7E0D6]">
                <CardContent className="pt-4">
                  <label className="text-xs text-[#5B616E] block mb-2 flex items-center gap-1"><Users className="w-3 h-3" /> Entity tags</label>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {(form.entities || []).map((e, idx) => (
                      <Badge key={idx} variant="secondary" className="text-[10px] gap-1 cursor-pointer"
                        onClick={() => setForm({ ...form, entities: form.entities.filter((_, i) => i !== idx) })}>
                        <span className="text-[8px] text-[#5B616E] uppercase">{e.type}</span> {e.text} ×
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-1">
                    <input
                      value={entityInput.text}
                      onChange={e => setEntityInput(prev => ({ ...prev, text: e.target.value }))}
                      placeholder="Entity name..."
                      className="flex-1 border border-[#E7E0D6] rounded-lg px-2 py-1 text-xs bg-white"
                      onKeyDown={e => {
                        if (e.key === 'Enter' && entityInput.text.trim()) {
                          e.preventDefault();
                          setForm(f => ({ ...f, entities: [...(f.entities || []), { text: entityInput.text.trim(), type: entityInput.type }] }));
                          setEntityInput(prev => ({ ...prev, text: '' }));
                        }
                      }}
                    />
                    <Select value={entityInput.type} onValueChange={v => setEntityInput(prev => ({ ...prev, type: v }))}>
                      <SelectTrigger className="w-24 text-xs h-7"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['person', 'org', 'location', 'event', 'concept'].map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" className="text-xs px-2" onClick={() => {
                      if (!entityInput.text.trim()) return;
                      setForm(f => ({ ...f, entities: [...(f.entities || []), { text: entityInput.text.trim(), type: entityInput.type }] }));
                      setEntityInput(prev => ({ ...prev, text: '' }));
                    }}>+</Button>
                  </div>
                  <Button size="sm" variant="ghost" className="text-xs mt-1 text-[#B89B5E]" onClick={async () => {
                    if (!form.content) return;
                    try {
                      const r = await aiExtrasAPI.entities({ content: form.title + ' ' + form.content.slice(0, 2000) });
                      const d = r.data;
                      const extracted = [
                        ...(d.people||[]).map(t => ({ text: t, type: 'person' })),
                        ...(d.organizations||[]).map(t => ({ text: t, type: 'org' })),
                        ...(d.locations||[]).map(t => ({ text: t, type: 'location' })),
                      ];
                      setForm(f => ({ ...f, entities: [...new Map([...(f.entities||[]), ...extracted].map(e => [e.text, e])).values()] }));
                      toast.success('Entities extracted');
                    } catch { toast.error('Entity extraction failed'); }
                  }}>
                    <Sparkles className="w-3 h-3 mr-1" /> AI extract entities
                  </Button>
                </CardContent>
              </Card>

              {/* Geo-tagging */}
              <Card className="bg-white border-[#E7E0D6]">
                <CardContent className="pt-4">
                  <label className="text-xs text-[#5B616E] block mb-2 flex items-center gap-1"><MapPin className="w-3 h-3" /> Location / geo-tag</label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-[#5B616E] block mb-0.5">Place name</label>
                      <Input className="text-xs" value={form.location?.name || ''} onChange={e => setForm({ ...form, location: { ...form.location, name: e.target.value } })} placeholder="London" />
                    </div>
                    <div>
                      <label className="text-[10px] text-[#5B616E] block mb-0.5">Country</label>
                      <Input className="text-xs" value={form.location?.country || ''} onChange={e => setForm({ ...form, location: { ...form.location, country: e.target.value } })} placeholder="UK" />
                    </div>
                    <div>
                      <label className="text-[10px] text-[#5B616E] block mb-0.5">Latitude</label>
                      <Input className="text-xs" type="number" step="any" value={form.location?.lat || ''} onChange={e => setForm({ ...form, location: { ...form.location, lat: e.target.value } })} placeholder="51.5" />
                    </div>
                    <div>
                      <label className="text-[10px] text-[#5B616E] block mb-0.5">Longitude</label>
                      <Input className="text-xs" type="number" step="any" value={form.location?.lon || ''} onChange={e => setForm({ ...form, location: { ...form.location, lon: e.target.value } })} placeholder="-0.1" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Custom metadata */}
              <Card className="bg-white border-[#E7E0D6]">
                <CardContent className="pt-4">
                  <label className="text-xs text-[#5B616E] block mb-1 flex items-center gap-1"><Database className="w-3 h-3" /> Custom metadata (JSON)</label>
                  <Textarea
                    className="text-xs font-mono"
                    rows={6}
                    value={form.custom_meta || ''}
                    onChange={e => setForm({ ...form, custom_meta: e.target.value })}
                    placeholder='{"author_title": "Senior Reporter", "word_count": 1200}'
                    data-testid="custom-meta-input"
                  />
                  <p className="text-[10px] text-[#5B616E] mt-0.5">Arbitrary key-value metadata. Must be valid JSON or will be ignored on save.</p>
                </CardContent>
              </Card>

              {/* Fact-check checklist */}
              {isEdit && (
                <Card className="bg-white border-[#E7E0D6]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Fact-check checklist
                      <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded-full ${factCheckStatus === 'verified' ? 'bg-green-50 text-green-700' : factCheckStatus === 'in-progress' ? 'bg-amber-50 text-amber-700' : 'bg-[#E7E0D6] text-[#5B616E]'}`}>{factCheckStatus}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {factChecks.map((f, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <input type="checkbox" checked={!!f.verified} onChange={async e => {
                          const updated = [...factChecks];
                          updated[i] = { ...f, verified: e.target.checked };
                          setFactChecks(updated);
                          const r = await factCheckAPI.update(id, { fact_checks: updated });
                          setFactCheckStatus(r.data.fact_check_status);
                        }} className="rounded" />
                        <span className={`text-xs flex-1 ${f.verified ? 'line-through text-[#5B616E]' : ''}`}>{f.claim}</span>
                        <button onClick={async () => {
                          const updated = factChecks.filter((_, j) => j !== i);
                          setFactChecks(updated);
                          const r = await factCheckAPI.update(id, { fact_checks: updated });
                          setFactCheckStatus(r.data.fact_check_status);
                        }} className="text-red-500 text-xs">×</button>
                      </div>
                    ))}
                    <div className="flex gap-1 mt-2">
                      <input value={newFact} onChange={e => setNewFact(e.target.value)} placeholder="Add claim to verify..." className="flex-1 border border-[#E7E0D6] rounded px-2 py-1 text-xs" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (newFact.trim()) { const updated = [...factChecks, { claim: newFact.trim(), verified: false }]; setFactChecks(updated); setNewFact(''); factCheckAPI.update(id, { fact_checks: updated }).then(r => setFactCheckStatus(r.data.fact_check_status)).catch(() => {}); } } }} />
                      <Button size="sm" variant="outline" className="text-xs" onClick={() => { if (newFact.trim()) { const updated = [...factChecks, { claim: newFact.trim(), verified: false }]; setFactChecks(updated); setNewFact(''); factCheckAPI.update(id, { fact_checks: updated }).then(r => setFactCheckStatus(r.data.fact_check_status)).catch(() => {}); } }}>+</Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Internal link suggestions */}
              {isEdit && internalLinkSuggestions.length > 0 && (
                <Card className="bg-white border-[#E7E0D6]">
                  <CardHeader className="pb-2"><CardTitle className="text-xs flex items-center gap-1"><Link2 className="w-3 h-3" /> Internal link suggestions</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {internalLinkSuggestions.map(s => (
                        <div key={s.id} className="flex items-center justify-between text-xs p-2 bg-[#FBF7EF] rounded-lg">
                          <span className="truncate flex-1">{s.title}</span>
                          <button onClick={() => { navigator.clipboard.writeText(`[${s.title}](/article/${s.id})`); toast.success('Markdown link copied'); }} className="ml-2 text-[#B89B5E] hover:underline text-[10px] flex-shrink-0">Copy MD link</button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Inline annotations */}
              {isEdit && (
                <Card className="bg-white border-[#E7E0D6]">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-xs flex items-center gap-1"><MessageCircle className="w-3 h-3" /> Annotations ({annotations.length})</CardTitle>
                      <Button size="sm" variant="ghost" className="text-xs h-6 px-2" onClick={() => setShowAnnotationPanel(v => !v)}>
                        {showAnnotationPanel ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      </Button>
                    </div>
                  </CardHeader>
                  {showAnnotationPanel && (
                    <CardContent className="space-y-3">
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {annotations.map(ann => (
                          <div key={ann.id} className={`p-2 rounded-lg border text-[10px] ${ann.is_resolved ? 'border-[#E7E0D6] opacity-60' : 'border-amber-200 bg-amber-50'}`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-[8px] uppercase tracking-wide text-[#5B616E]">{ann.annotation_type}</span>
                              <div className="flex gap-1">
                                {!ann.is_resolved && (
                                  <button className="text-green-600 hover:underline text-[9px]" onClick={async () => {
                                    await annotationsAPI.update(ann.id, { is_resolved: true });
                                    setAnnotations(prev => prev.map(a => a.id === ann.id ? { ...a, is_resolved: true } : a));
                                  }}>Resolve</button>
                                )}
                                <button className="text-red-500 hover:underline text-[9px]" onClick={async () => {
                                  await annotationsAPI.delete(ann.id);
                                  setAnnotations(prev => prev.filter(a => a.id !== ann.id));
                                }}>×</button>
                              </div>
                            </div>
                            {ann.text_selection && <p className="italic text-[#5B616E] mb-1 border-l-2 border-[#B89B5E] pl-1">"{ann.text_selection}"</p>}
                            <p>{ann.note}</p>
                            <p className="text-[8px] text-[#5B616E] mt-1">{ann.author_email} · {ann.created_at ? new Date(ann.created_at).toLocaleString() : ''}</p>
                          </div>
                        ))}
                        {annotations.length === 0 && <p className="text-[10px] text-[#5B616E] text-center py-2">No annotations yet.</p>}
                      </div>
                      <div className="border-t border-[#E7E0D6] pt-3 space-y-2">
                        <Select value={newAnnotation.annotation_type} onValueChange={v => setNewAnnotation(a => ({ ...a, annotation_type: v }))}>
                          <SelectTrigger className="text-xs h-7"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {['editorial', 'factcheck', 'legal', 'style'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input className="text-xs" placeholder="Highlighted text (optional)" value={newAnnotation.text_selection} onChange={e => setNewAnnotation(a => ({ ...a, text_selection: e.target.value }))} />
                        <Textarea rows={2} className="text-xs" placeholder="Annotation note..." value={newAnnotation.note} onChange={e => setNewAnnotation(a => ({ ...a, note: e.target.value }))} />
                        <Button size="sm" className="w-full text-xs bg-[#121316] text-white" onClick={async () => {
                          if (!newAnnotation.note.trim()) return;
                          try {
                            const r = await annotationsAPI.create(id, newAnnotation);
                            setAnnotations(prev => [...prev, r.data]);
                            setNewAnnotation({ text_selection: '', note: '', annotation_type: 'editorial' });
                            toast.success('Annotation added');
                          } catch { toast.error('Failed to add annotation'); }
                        }}>Add Annotation</Button>
                      </div>
                    </CardContent>
                  )}
                </Card>
              )}
            </TabsContent>

            {/* AI TAB */}
            <TabsContent value="ai" className="space-y-4 mt-3">
              <Card className="bg-white border-[#E7E0D6] border-dashed">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xs flex items-center gap-1"><Sparkles className="w-3 h-3 text-[#B89B5E]" /> AI Assistant</CardTitle>
                    <AIModelSelector value={aiModel} onChange={setAiModel} compact />
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <textarea value={aiPrompt} onChange={e => setAiPrompt(e.target.value)} placeholder="Describe the article you want AI to write..." className="w-full border border-[#E7E0D6] rounded-lg px-3 py-2 text-xs bg-white resize-none" rows={3} />
                  <div className="flex gap-2">
                    <Button data-testid="ai-generate-button" size="sm" variant="outline" onClick={handleAIGenerate} disabled={aiLoading || !aiPrompt.trim()} className="flex-1 text-xs">
                      <Wand2 className="w-3 h-3 mr-1" /> {aiLoading ? 'Working...' : 'Generate'}
                    </Button>
                    <Button data-testid="ai-summarize-button" size="sm" variant="outline" onClick={handleAISummarize} disabled={aiLoading || !form.content} className="flex-1 text-xs">
                      <Sparkles className="w-3 h-3 mr-1" /> Summarize
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={handlePersistedAutoTag} disabled={autoTagLoading || !form.content} className="flex-1 text-xs">
                      {autoTagLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Tag className="w-3 h-3 mr-1" />} Auto-Tag
                    </Button>
                    <Button size="sm" variant="outline" onClick={handleSEOGen} disabled={seoGenLoading || !form.title} className="flex-1 text-xs">
                      {seoGenLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Search className="w-3 h-3 mr-1" />} SEO Gen
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={handleAltText} disabled={!form.featured_image} className="flex-1 text-xs">
                      <ImageIcon className="w-3 h-3 mr-1" /> Alt-text
                    </Button>
                    <Button size="sm" variant="outline" onClick={handleHeadlineExperiment} className="flex-1 text-xs">
                      <Sparkles className="w-3 h-3 mr-1" /> Headline A/B
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={handleHeadlineSuggest} disabled={headlineLoading || (!form.title && !form.content)} className="flex-1 text-xs">
                      {headlineLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />} Suggest headlines
                    </Button>
                    <Button size="sm" variant="outline" onClick={handleSocialPosts} disabled={socialLoading || !form.title} className="flex-1 text-xs">
                      {socialLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />} Social posts
                    </Button>
                  </div>
                  <p className="text-[9px] text-[#5B616E] bg-amber-50 p-2 rounded-lg"><AlertTriangle className="w-3 h-3 inline mr-1" />AI content requires human review before publishing.</p>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Workflow Action Dialog */}
      <Dialog open={showWorkflowDialog} onOpenChange={setShowWorkflowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="capitalize">Workflow: {workflowAction?.replace('_', ' ')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {workflowAction === 'schedule' && (
              <div>
                <label className="text-sm font-medium mb-1 block">Schedule Date & Time</label>
                <Input type="datetime-local" value={form.scheduled_at?.slice(0, 16) || ''} onChange={e => setForm({ ...form, scheduled_at: e.target.value })} />
              </div>
            )}
            <Textarea value={workflowComment} onChange={e => setWorkflowComment(e.target.value)} placeholder="Add a comment (optional)..." rows={3} />
            {workflowAction === 'kill' && (
              <p className="text-xs text-[#B3261E] bg-red-50 p-2 rounded-lg"><AlertTriangle className="w-3 h-3 inline mr-1" />This will immediately retract the article from public view.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowWorkflowDialog(false)}>Cancel</Button>
            <Button onClick={handleWorkflowAction} className="bg-[#121316] text-white hover:bg-[#1B1D22]">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Versions Dialog */}
      <Dialog open={showVersions} onOpenChange={setShowVersions}>
        <DialogContent className="sm:max-w-lg max-h-[70vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Version History</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {versions.length > 1 && (
              <p className="text-xs text-[#5B616E] bg-[#FBF7EF] rounded-lg px-3 py-2">
                Compare any two versions: click <strong>Compare</strong> on one version, then on another.
              </p>
            )}
            {versions.length > 0 ? versions.map(v => (
              <div key={v.id} className="flex items-center gap-3 p-3 rounded-lg border border-[#E7E0D6] hover:bg-[#FBF7EF] transition-colors">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${diffV1 === v.version || diffV2 === v.version ? 'bg-[#B89B5E] text-white' : 'bg-[#B89B5E]/10 text-[#B89B5E]'}`}>v{v.version}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{v.title || 'Untitled'}</p>
                  <p className="text-[10px] text-[#5B616E]">{v.user_name || v.user_email || 'Unknown'} &middot; {v.created_at ? new Date(v.created_at).toLocaleString() : ''}</p>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" className="text-xs" onClick={() => {
                    if (!diffV1) { setDiffV1(v.version); }
                    else if (!diffV2 && v.version !== diffV1) { handleShowDiff(Math.min(diffV1, v.version), Math.max(diffV1, v.version)); setDiffV1(null); }
                    else { setDiffV1(v.version); setDiffV2(null); }
                  }}>
                    Compare
                  </Button>
                  <Button size="sm" variant="outline" className="text-xs" onClick={() => handleRollback(v.version)}>
                    <RotateCcw className="w-3 h-3 mr-1" /> Restore
                  </Button>
                </div>
              </div>
            )) : (
              <p className="text-sm text-[#5B616E] text-center py-8">No versions saved yet. Versions are auto-saved when you update the article.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Diff (Track Changes) Dialog */}
      <Dialog open={showDiff} onOpenChange={v => { setShowDiff(v); if (!v) { setDiffV1(null); setDiffV2(null); setDiffData(null); } }}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Track Changes: v{Math.min(diffData?.v1 ?? 0, diffData?.v2 ?? 0)} → v{Math.max(diffData?.v1 ?? 0, diffData?.v2 ?? 0)}</DialogTitle>
          </DialogHeader>
          {diffLoading ? (
            <div className="flex-1 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#B89B5E]" /></div>
          ) : diffData ? (
            <div className="flex-1 overflow-y-auto space-y-3">
              {diffData.title_changed && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                  <p className="font-medium text-amber-800 mb-1">Title changed</p>
                  <p className="line-through text-red-600 text-xs">{diffData.title_v1}</p>
                  <p className="text-green-600 text-xs">{diffData.title_v2}</p>
                </div>
              )}
              <div className="bg-[#121316] rounded-lg p-3 overflow-x-auto">
                <div className="font-mono text-xs space-y-0.5">
                  {(diffData.diff_lines || []).map((line, i) => {
                    let cls = 'text-[#5B616E]';
                    if (line.startsWith('+++') || line.startsWith('---')) cls = 'text-[#5B616E] font-semibold';
                    else if (line.startsWith('+')) cls = 'bg-green-900/40 text-green-300 block px-1 rounded';
                    else if (line.startsWith('-')) cls = 'bg-red-900/40 text-red-300 block px-1 rounded line-through';
                    else if (line.startsWith('@@')) cls = 'text-[#B89B5E] font-semibold';
                    return <span key={i} className={cls}>{line || ' '}{'\n'}</span>;
                  })}
                  {(!diffData.diff_lines || diffData.diff_lines.length === 0) && (
                    <p className="text-[#5B616E] text-center py-4">No content differences</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-green-600">+{diffData.additions} additions</span>
                <span className="text-red-500">−{diffData.deletions} deletions</span>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            {diffData && (
              <Button variant="outline" className="text-xs" onClick={() => {
                // Accept: apply v2 content to current form
                const v2n = Math.max(diffData.v1, diffData.v2);
                const v2ver = versions.find(v => v.version === v2n);
                if (v2ver) {
                  setForm(f => ({ ...f, title: v2ver.title || f.title, content: v2ver.content || f.content }));
                  toast.success('Changes accepted — review and save to persist');
                }
                setShowDiff(false);
              }}>Accept v{Math.max(diffData?.v1 ?? 0, diffData?.v2 ?? 0)}</Button>
            )}
            <Button variant="ghost" className="text-xs" onClick={() => setShowDiff(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Headline suggestions popover */}
      <Dialog open={headlineOpen} onOpenChange={setHeadlineOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Sparkles className="w-4 h-4 text-[#B89B5E]" />
              Suggested headlines
            </DialogTitle>
          </DialogHeader>
          {headlineLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-[#B89B5E]" />
            </div>
          ) : headlineSuggestions.length === 0 ? (
            <p className="text-sm text-[#5B616E] py-4">
              No suggestions yet — add some draft content and try again.
            </p>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {headlineSuggestions.map((s, i) => {
                const text = typeof s === 'string' ? s : (s.headline || '');
                const style = typeof s === 'object' ? s.style : null;
                const rationale = typeof s === 'object' ? s.rationale : null;
                if (!text) return null;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => applyHeadline(text)}
                    className="w-full text-left border border-[#E7E0D6] rounded-xl px-4 py-3 hover:border-[#B89B5E] hover:bg-[#FBF7EF] transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm font-semibold news-masthead leading-snug">{text}</p>
                      {style && (
                        <Badge variant="outline" className="text-[9px] shrink-0 capitalize">{style}</Badge>
                      )}
                    </div>
                    {rationale && (
                      <p className="text-[11px] text-[#5B616E] mt-1">{rationale}</p>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={handleHeadlineSuggest} disabled={headlineLoading}>
              <RotateCcw className="w-3 h-3 mr-1" /> Regenerate
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setHeadlineOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Social posts dialog */}
      <Dialog open={socialOpen} onOpenChange={setSocialOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Send className="w-4 h-4 text-[#B89B5E]" />
              Social posts
            </DialogTitle>
          </DialogHeader>
          {socialLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-[#B89B5E]" />
            </div>
          ) : Object.keys(socialPosts).length === 0 ? (
            <p className="text-sm text-[#5B616E] py-4">
              No posts generated yet. Click "Regenerate" to draft a set.
            </p>
          ) : (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {Object.entries(socialPosts).map(([platform, text]) => (
                <div key={platform} className="border border-[#E7E0D6] rounded-xl p-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <Badge variant="outline" className="capitalize text-[10px]">{platform}</Badge>
                    <div className="flex items-center gap-2 text-[10px] text-[#5B616E]">
                      <span>{(text || '').length} chars</span>
                      <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2" onClick={() => copyToClipboard(text || '')}>
                        <Copy className="w-3 h-3 mr-1" /> Copy
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm whitespace-pre-wrap text-[#1B1D22]">{text}</p>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={handleSocialPosts} disabled={socialLoading}>
              <RotateCcw className="w-3 h-3 mr-1" /> Regenerate
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSocialOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
